import api from "./axios";

export type MultiAccountType = "full" | "partial";

export type AvailableCompany = {
  id: number;
  company_name: string;
};

export interface MeResponse {
  id: number;
  email: string;
  fullname?: string;
  role: "admin" | "client" | "user";
  company_id: number | null;
  company_name?: string | null;
  multi_account_type?: MultiAccountType | null;
  accessible_companies?: AvailableCompany[];
  subscription_expires_on?: string | null;
}

export async function login(email: string, password: string) {
  await api.post("/auth/login", { email, password });
}

export async function logout() {
  await api.post("/auth/logout");
}

export async function fetchMe(): Promise<MeResponse> {
  const { data } = await api.get<MeResponse>("/auth/me");
  return data;
}

export async function fetchAvailableCompanies(): Promise<AvailableCompany[]> {
  const { data } = await api.get<AvailableCompany[] | { items?: AvailableCompany[]; companies?: AvailableCompany[] }>(
    "/auth/available-companies",
  );

  if (Array.isArray(data)) return data;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.companies)) return data.companies;
  return [];
}

export async function updateProfile(
  userId: number,
  payload: { fullname?: string | null; password?: string },
) {
  const body: Record<string, unknown> = {};
  if (payload.fullname !== undefined) body.fullname = payload.fullname;
  if (payload.password) body.password = payload.password;
  const { data } = await api.patch<MeResponse>(`/users/${userId}`, body);
  return data;
}

export type RegisterClientPayload = {
  company_name: string;
  inn?: string | null;
  city: string;
  fullname: string;
  email: string;
  phone: string;
  password: string;
  password_confirm: string;
};

export type RegisterClientResponse = {
  message?: string;
  confirmation_token?: string;
  confirmation_url?: string;
};

export type ForgotPasswordResponse = {
  message?: string;
  reset_token?: string;
  reset_url?: string;
};

export async function fetchRegistrationCities(): Promise<string[]> {
  const { data } = await api.get<string[] | { cities?: string[]; items?: string[] }>(
    "/auth/registration-cities",
  );

  if (Array.isArray(data)) return data;
  if (Array.isArray(data.cities)) return data.cities;
  if (Array.isArray(data.items)) return data.items;
  return [];
}

export async function registerClient(payload: RegisterClientPayload) {
  const { data } = await api.post<RegisterClientResponse>("/auth/register-client", payload);
  return data;
}

export async function confirmAccount(token: string) {
  const { data } = await api.get<{ message?: string }>("/auth/confirm-account", {
    params: { token },
  });
  return data;
}

export async function forgotPassword(email: string) {
  const { data } = await api.post<ForgotPasswordResponse>("/auth/forgot-password", { email });
  return data;
}

export async function resetPassword(token: string, password: string) {
  const { data } = await api.post<{ message?: string }>("/auth/reset-password", {
    token,
    password,
  });
  return data;
}
